import type { RefObject } from 'preact';
import { useMemo, useState, useEffect, useRef } from 'preact/hooks';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { dragToScroll, findElementByKey } from '../utils/PRIVATE_collectionUtils';
import { ViewportConfig } from '../hooks/UNSAFE_useViewportIntersect';
import { useReorderable } from '../hooks/PRIVATE_useReorderable';
import { scrollToVisible, LIST_ITEM_SELECTOR } from '../PRIVATE_List';
import { ListProps } from './List.types';

type useListViewReorderProps<K, D> = Pick<ListProps<K, D>, 'onReorder'> & {
  rootRef: RefObject<HTMLDivElement>;
  viewportConfig?: ViewportConfig;
};

export const useListViewReorder = <K extends string | number, D>({
  onReorder,
  rootRef,
  viewportConfig
}: useListViewReorderProps<K, D>) => {
  const isReorderEnabled = onReorder != undefined;

  const [dragKey, setDragKey] = useState<K | undefined>(undefined);
  const { reorderProps, reorderContext, reorderInstructionsId, reorderInstructions } =
    useReorderable({
      onReorder,
      rootRef,
      itemSelector: LIST_ITEM_SELECTOR,
      isDisabled: !isReorderEnabled,
      columns: 1
    });

  // ensure the current item is scrolled into view during keyboard reordering,
  // disable that during mouse reordering
  const reorderScrollIntoView = useRef(false);
  useEffect(() => {
    if (isReorderEnabled && reorderScrollIntoView.current) {
      const rootElement = rootRef.current as HTMLElement;
      const elem = findElementByKey(
        rootElement,
        reorderContext.currentItemKey.current as K,
        LIST_ITEM_SELECTOR
      );
      if (elem) {
        const scroller = viewportConfig?.scrollerRef.current || rootElement;
        // make sure item is visible
        scrollToVisible(elem, scroller, 0);
        reorderScrollIntoView.current = false;
      }
    }
  });

  const reorderItemProps = useMemo(() => {
    return { dragKey, setDragKey, onReorder, rootRef };
  }, [dragKey, onReorder, rootRef]);

  if (!isReorderEnabled) {
    return {
      reorderProps,
      reorderContext,
      reorderInstructionsId,
      reorderInstructions
    } as const;
  }

  const handleKeyDown = (event: KeyboardEvent) => {
    // listview only do keyboard reordering with arrow up/down keys
    if (
      isReorderEnabled &&
      (event.key === 'ArrowUp' || event.key === 'ArrowDown') &&
      (event.metaKey || event.ctrlKey) &&
      event.shiftKey
    ) {
      reorderScrollIntoView.current = true;
      reorderProps.onKeyDown?.(event);
    }
  };

  const handleDragOver = (event: DragEvent) => {
    if (dragKey !== undefined) {
      reorderScrollIntoView.current = false;
      dragToScroll(event, dragKey !== undefined, rootRef.current);
    }
  };

  const reorderEventProps = mergeProps({ onKeyDown: handleKeyDown, onDragOver: handleDragOver });

  return {
    reorderProps: reorderEventProps,
    reorderItemProps,
    reorderContext,
    reorderInstructionsId,
    reorderInstructions
  } as const;
};
